/**
 * Centralized z-index hierarchy for Atlas frontend.
 *
 * Two bands:
 *   1 000–1 400   App UI layers (below Leaflet pane overrides — map features
 *                 stay visible above persistent side panels by design)
 *   10 000+       Leaflet pane CSS overrides and blocking UI
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  Token            Value    What lives here
 * ─────────────────────────────────────────────────────────────────────────────
 *  MAP_LAYER_*   100–500    Leaflet WMS / GeoJSON rendering order (layer props)
 *  MAP_CONTROL     1 000    Floating map controls (ruler, elevation, move btns)
 *  PANEL           1 100    Sliding side panels (stats, legend drawer, report)
 *  TEMPORAL        1 200    Temporal control slider
 *  TOOLS_MENU      1 300    Tool selection panel
 *  ACTION_BAR      1 400    Action bars that open/close panels & search bar
 *  ─── Leaflet pane overrides (mapStyles.css): 10 000–10 002 ───────────────
 *       Intentionally above APP UI so drawings / markers / popups remain
 *       visible above persistent side panels.
 *  MAP_OVERLAY_CONTROL 10 003 Map controls that must receive pointer input
 *  MODAL_OVERLAY      10 100 Full-screen blocking backdrop
 *  MODAL              10 200 Blocking dialog content
 *  TOAST              10 300 Transient notifications (always on top)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Tailwind usage  → z-panel, z-modal, z-modal-overlay, z-toast, etc.
 * Styled-comp     → import { Z } from "../../global/constants/zIndex"
 * Inline style    → zIndex: Z.MAP_CONTROL
 */

export const LEAFLET_PANE_MAX_Z_INDEX = 10_002;

export const Z = {
  // Map layer rendering order (passed as `zIndex` prop to Leaflet components)
  MAP_LAYER_BASE:    100,
  MAP_LAYER_DEFAULT: 400,
  MAP_LAYER_WINDY:   450,
  MAP_LAYER_TOP:     500,

  // Application UI layers
  MAP_CONTROL: 1_000,
  PANEL:       1_100,
  TEMPORAL:    1_200,
  TOOLS_MENU:  1_300,
  ACTION_BAR:  1_400,

  // Leaflet DOM pane CSS overrides are at 10 000–10 002 (see mapStyles.css).
  // Interactive controls that sit inside the map but must receive pointer
  // input need to clear those panes without behaving like blocking modals.
  MAP_OVERLAY_CONTROL: LEAFLET_PANE_MAX_Z_INDEX + 1,
  MODAL_OVERLAY: 10_100,
  MODAL:         10_200,
  TOAST:         10_300,
} as const;

export type ZIndex = (typeof Z)[keyof typeof Z];
